import { Card, Flex, SecureCopy, SimpleTable, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const SecureCopyPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Title level={4}>SecureCopy</Title>
        <Text size="sm" style={{ marginBottom: 30 }}>
          The SecureCopy component in Djuno Design provides a simple, secure way
          to copy text to the clipboard. It offers customization options for
          styling, size, and interaction states such as "copy" and "hide." It
          can also include icons for enhanced user experience.
        </Text>

        <Title level={5}>Variants and Customization</Title>
        <Text size="sm">
          The SecureCopy component offers the following customization options:
        </Text>
        <ul className="marker:text-sky-400 list-disc pl-6">
          <li>
            <Text size="sm">
              <Text strong>Type:</Text> Choose between the "copy" or "hide"
              action, determining whether the component copies text or toggles
              the visibility of sensitive content.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text strong>Custom Classes:</Text> Apply additional class names
              to style the text and icon elements as needed.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text strong>Interactivity:</Text> SecureCopy supports user
              interactions such as clicking to copy or toggling text visibility
              for secure information.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Size Variants
              </Text>
              : Choose between different sizes (small, medium, large) to fit
              different layout requirements.
            </Text>
          </li>
        </ul>

        {/* SecureCopy Examples */}
        <Flex direction="col" className="mt-5">
          <Title level={5}>Examples</Title>
          <ExpandableCard
            title="SecureCopy"
            codeViewerProps={{
              code: SecureCopyExample1,
            }}
          >
            <Flex direction="col" className="gap-5 w-full ">
              <Flex items="end" className="gap-3 w-full flex ">
                <SecureCopy text="Djuno Design" type="hide" uiSize="small" />
                <SecureCopy text="Djuno Design" type="hide" uiSize="medium" />
                <SecureCopy text="Djuno Design" type="hide" uiSize="large" />
              </Flex>
              <Flex items="end" className="gap-3 w-full flex ">
                <SecureCopy text="Djuno Design" type="copy" uiSize="small" />
                <SecureCopy text="Djuno Design" type="copy" uiSize="medium" />
                <SecureCopy text="Djuno Design" type="copy" uiSize="large" />
              </Flex>
            </Flex>
          </ExpandableCard>
        </Flex>

        {/* SecureCopy API Table */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5}>API</Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* text */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">text</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">The text to be copied or hidden.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">type</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the component's action: "copy" or "hide."
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ISecureCopy.ts#L35"}
                        size="xs"
                        code
                      >
                        ActionTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">copy</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* iconClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">iconClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Class name for the icon element.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      {" "}
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* textClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">textClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Class name for the text element.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      {" "}
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* showText */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">showText</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Whether to show the text content or not. Applicable only
                        for "hide" type.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      {" "}
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the size of the input field (height, padding,
                        etc.).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* copyable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">copyable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Enables copy-to-clipboard functionality, allowing users
                        to copy the input value with an optional custom copy
                        behavior.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L67"}
                        size="xs"
                        code
                      >
                        {`CopyableProp`}
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const SecureCopyExample1 = `<Flex direction="col" className="gap-5 w-full ">
  <Flex items="end" className="gap-3 w-full flex ">
    <SecureCopy text="Djuno Design" type="hide" uiSize="small" />
    <SecureCopy text="Djuno Design" type="hide" uiSize="medium" />
    <SecureCopy text="Djuno Design" type="hide" uiSize="large" />
  </Flex>
  <Flex items="end" className="gap-3 w-full flex ">
    <SecureCopy text="Djuno Design" type="copy" uiSize="small" />
    <SecureCopy text="Djuno Design" type="copy" uiSize="medium" />
    <SecureCopy text="Djuno Design" type="copy" uiSize="large" />
  </Flex>
</Flex>`;

export default SecureCopyPage;
